/**
 * FloorPlanUtils.js
 * Pure utility / helper functions for the Floor Plan Designer feature.
 * Extracted from FloorPlanDesignerPage.jsx to improve maintainability.
 * All functions are stateless and have no side-effects.
 */

import { SNAP_THRESHOLD } from './FloorPlanConstants';
import * as Sentry from '@sentry/react';

// ── Geometry helpers ──────────────────────────────────────────────────────────

/**
 * Pre-extracts all snap-worthy geometry points from an array of FloorPlanElement objects.
 * Parses each element's geometryJson once up-front and returns a flat list of {x, y} points.
 *
 * This is intended to be called with `useMemo` in the consuming component so that the
 * JSON parsing only happens when `elements` changes, rather than on every mouse-move event.
 *
 * @param {Array} elements - Current array of FloorPlanElement objects.
 * @returns {{ x: number, y: number }[]} - Flat list of all snap-worthy canvas points.
 */
export const extractSnapPoints = (elements) => {
    const allPoints = [];

    for (const elem of elements) {
        if (!elem.geometryJson) continue;
        try {
            // Accept both raw JSON strings (from the server) and already-parsed objects
            const geom = typeof elem.geometryJson === 'string'
                ? JSON.parse(elem.geometryJson)
                : elem.geometryJson;

            // Wall format: { x1, y1, x2, y2, thickness }
            if (geom.x1 !== undefined) {
                allPoints.push({ x: geom.x1, y: geom.y1 }, { x: geom.x2, y: geom.y2 });
            }

            // Polygon / room format: { points: [x0,y0,x1,y1,...], closed: true }
            // Konva flat interleaved format — NOT nested [[x,y], ...] pairs
            if (geom.points) {
                for (let i = 0; i < geom.points.length; i += 2) {
                    allPoints.push({ x: geom.points[i], y: geom.points[i + 1] });
                }
            }

            // Point-based elements: door, window, marker, label, etc.
            if (geom.x !== undefined && geom.y !== undefined) {
                allPoints.push({ x: geom.x, y: geom.y });
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* skip elements with invalid/unparseable geometry */
        }
    }

    return allPoints;
};

/**
 * Finds the nearest pre-computed snap point within `threshold` pixels of (x, y).
 * Intended to be used together with `extractSnapPoints`: call `extractSnapPoints`
 * via `useMemo` when `elements` changes, then pass the memoized list here on
 * every mouse-move to avoid re-parsing geometry JSON on each event.
 *
 * @param {number} x - Candidate X coordinate in canvas-space pixels.
 * @param {number} y - Candidate Y coordinate in canvas-space pixels.
 * @param {{ x: number, y: number }[]} snapPoints - Pre-computed list from `extractSnapPoints`.
 * @param {number} [threshold] - Maximum snap distance in pixels.
 * @returns {{ x: number, y: number, snapped: boolean }}
 */
export const findSnapPointInList = (x, y, snapPoints, threshold = SNAP_THRESHOLD) => {
    let nearestDist = threshold;
    let snapX = x;
    let snapY = y;
    let snapped = false;

    for (const pt of snapPoints) {
        const dist = Math.sqrt((pt.x - x) ** 2 + (pt.y - y) ** 2);
        if (dist < nearestDist) {
            nearestDist = dist;
            snapX = pt.x;
            snapY = pt.y;
            snapped = true;
        }
    }

    return { x: snapX, y: snapY, snapped };
};

/**
 * Scans the existing element list and returns the nearest geometry point
 * within `threshold` pixels of the provided (x, y) coordinate.
 * Used to snap a newly drawn point to an existing wall endpoint or corner.
 *
 * @deprecated Prefer `extractSnapPoints` + `findSnapPointInList` to avoid
 *   re-parsing geometry JSON on every mouse-move event. This function is kept
 *   for backward compatibility only.
 *
 * @param {number} x - Candidate X coordinate in canvas-space pixels.
 * @param {number} y - Candidate Y coordinate in canvas-space pixels.
 * @param {Array}  elements - Current array of FloorPlanElement objects.
 * @param {number} [threshold] - Maximum snap distance in pixels.
 * @returns {{ x: number, y: number, snapped: boolean }}
 */
export const findSnapPoint = (x, y, elements, threshold = SNAP_THRESHOLD) => {
    return findSnapPointInList(x, y, extractSnapPoints(elements), threshold);
};

/**
 * Snaps `value` to the nearest multiple of `gridSize`.
 * Returns `value` unchanged when gridSize is 0 or negative.
 *
 * @param {number} value    - The raw pixel value to snap.
 * @param {number} gridSize - Grid cell size in pixels (0 = no snap).
 * @returns {number}
 */
export const snapToGrid = (value, gridSize) => {
    if (gridSize <= 0) return value;
    return Math.round(value / gridSize) * gridSize;
};

// ── Measurement helpers ───────────────────────────────────────────────────────

/**
 * Calculates the straight-line distance between two canvas points and converts
 * the result to real-world meters using the current scale.
 *
 * @param {number} x1             - Start X (pixels).
 * @param {number} y1             - Start Y (pixels).
 * @param {number} x2             - End X (pixels).
 * @param {number} y2             - End Y (pixels).
 * @param {number} metersPerPixel - Scale factor (e.g. 0.05 = 20px per metre).
 * @returns {string} Distance in metres, formatted to 2 decimal places.
 */
export const calcDistance = (x1, y1, x2, y2, metersPerPixel) => {
    const px = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    return (px * metersPerPixel).toFixed(2);
};

/**
 * Calculates the area enclosed by a flat polygon described by an interleaved
 * [x0, y0, x1, y1, ...] points array (Konva format) and converts the result
 * to real-world square metres.
 *
 * Uses the Shoelace / Gauss area formula.
 *
 * @param {number[]} points       - Interleaved [x, y, x, y, ...] coordinates.
 * @param {number}   metersPerPixel - Scale factor.
 * @returns {number} Area in square metres.
 */
export const calcPolygonArea = (points, metersPerPixel) => {
    let area = 0;
    const n = points.length / 2;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += points[i * 2] * points[j * 2 + 1];
        area -= points[j * 2] * points[i * 2 + 1];
    }
    return Math.abs(area / 2) * metersPerPixel * metersPerPixel;
};

// ── Unit conversion helpers ───────────────────────────────────────────────────

/**
 * Converts a canvas pixel length to a real-world measurement in the requested unit.
 *
 * @param {number} px             - Length in canvas pixels.
 * @param {number} metersPerPixel - Scale factor.
 * @param {'m'|'cm'|'mm'} unit   - Target unit of measurement.
 * @returns {number}
 */
export const pxToUnit = (px, metersPerPixel, unit) => {
    const meters = px * metersPerPixel;
    if (unit === 'cm') return meters * 100;
    if (unit === 'mm') return meters * 1000;
    return meters;
};

/**
 * Converts a real-world measurement to canvas pixels.
 *
 * @param {number} value          - Value in the given unit.
 * @param {number} metersPerPixel - Scale factor.
 * @param {'m'|'cm'|'mm'} unit   - Source unit of measurement.
 * @returns {number}
 */
export const unitToPx = (value, metersPerPixel, unit) => {
    let meters = value;
    if (unit === 'cm') meters = value / 100;
    else if (unit === 'mm') meters = value / 1000;
    return meters / metersPerPixel;
};
